// Which sections of the prospect detail screen exist at a given stage, and
// which one an agent should land on.
//
// The screen grew section by section — fields, contract, station configuration,
// journey, edit history — until the part that needed acting on was several
// screens down. Splitting it into tabs only helps if a tab never opens empty and
// the first one shown is the one holding the work, so both are decided here
// rather than in the page.

import { OnboardingState } from "../types/onboarding";
import {
  hasContractHistory,
  isStationConfigStage,
  isTerminal,
} from "./onboardingStates";

export type ProspectTabKey =
  | "details"
  | "contract"
  | "stations"
  | "progress"
  | "history";

// The contract panel hides itself once a prospect resigns, and its activity is
// only fetched where a contract exists — so the tab follows the same rule
// instead of offering an empty page.
export const hasContractTab = (state: OnboardingState): boolean =>
  hasContractHistory(state) && !isTerminal(state);

// Station access data exists from "contract signed" onwards (the same gate the
// panel itself applies).
export const hasStationsTab = (state: OnboardingState): boolean =>
  isStationConfigStage(state);

// In display order. Details, progress and the edit history are always there:
// every prospect has submitted fields, every prospect has a journey — including
// a resigned one, where the journey is the only place the reason is recorded —
// and the audit answers "who changed this" at any stage.
export const prospectTabKeys = (state: OnboardingState): ProspectTabKey[] => [
  "details",
  ...(hasContractTab(state) ? (["contract"] as ProspectTabKey[]) : []),
  ...(hasStationsTab(state) ? (["stations"] as ProspectTabKey[]) : []),
  "progress",
  "history",
];

// The tab that carries the next thing to do at this stage, so the screen opens
// on the work rather than on the submitted form. Terminal and pre-contract
// prospects have no panel to act in, so they open on the fields.
export const initialProspectTab = (state: OnboardingState): ProspectTabKey => {
  if (hasStationsTab(state)) return "stations";
  if (hasContractTab(state)) return "contract";
  return "details";
};

export const prospectTabLabel = (key: ProspectTabKey): string => {
  switch (key) {
    case "contract":
      return "Contract";
    case "stations":
      return "Stations";
    case "progress":
      return "Progress";
    case "history":
      return "Edit history";
    default:
      return "Details";
  }
};
